// /src/engine/ldrawMath.ts

import * as THREE from 'three';

// LDraw uses -Y as "up". Three.js uses +Y as up.
// LDraw is right-handed with -Y as "up" (so Z is opposite of three.js as well).
// Convert via a 180° rotation around X: (x, y, z) -> (x, -y, -z)
const S = new THREE.Matrix4().makeScale(1, -1, -1);

export function ldrawToThreeMatrix(mL: THREE.Matrix4): THREE.Matrix4 {
  // MT = S * ML * S
  return new THREE.Matrix4().multiplyMatrices(S, new THREE.Matrix4().multiplyMatrices(mL, S));
}

export function threeToLdrawMatrix(mT: THREE.Matrix4): THREE.Matrix4 {
  // ML = S * MT * S
  return new THREE.Matrix4().multiplyMatrices(S, new THREE.Matrix4().multiplyMatrices(mT, S));
}

export function parseLdrawLineType1(line: string):
  | { color: number; partFile: string; matrixL: THREE.Matrix4 }
  | null {
  // Format:
  // 1 <color> x y z a b c d e f g h i <part.dat>
  const t = line.trim().split(/\s+/);
  if (t.length < 15) return null;
  if (t[0] !== '1') return null;
  const color = Number(t[1]);
  const nums = t.slice(2, 14).map(Number);
  if (nums.some((n) => Number.isNaN(n))) return null;

  const [x, y, z, a, b, c, d, e, f, g, h, i] = nums;
  const partFile = t.slice(14).join(' ');

  // Row-major in file, but THREE.Matrix4 stores column-major.
  // We can set elements explicitly using set(n11, n12, n13, n14, ...)
  const m = new THREE.Matrix4().set(
    a, b, c, x,
    d, e, f, y,
    g, h, i, z,
    0, 0, 0, 1
  );
  return { color, partFile, matrixL: m };
}

export function matrixToLdrawType1(
  color: number,
  partFile: string,
  matrixT: THREE.Matrix4
): string {
  const mL = threeToLdrawMatrix(matrixT);
  const e = mL.elements;
  // elements are column-major:
  // [ 0  4  8 12 ]
  // [ 1  5  9 13 ]
  // [ 2  6 10 14 ]
  // [ 3  7 11 15 ]

  const a = e[0], b = e[4], c = e[8], x = e[12];
  const d = e[1], en = e[5], f = e[9], y = e[13];
  const g = e[2], h = e[6], i = e[10], z = e[14];

  // Keep output stable & readable
  const fmt = (n: number) => {
    const r = Math.abs(n) < 1e-10 ? 0 : n;
    return Number.isInteger(r) ? String(r) : r.toFixed(6).replace(/\.?(0+)$/, '');
  };

  return `1 ${color} ${fmt(x)} ${fmt(y)} ${fmt(z)} ${fmt(a)} ${fmt(b)} ${fmt(c)} ${fmt(d)} ${fmt(en)} ${fmt(f)} ${fmt(g)} ${fmt(h)} ${fmt(i)} ${partFile}`;
}

export function matrixFromArray(arr: number[]): THREE.Matrix4 {
  const m = new THREE.Matrix4();
  m.fromArray(arr);
  return m;
}

export function matrixToArray(m: THREE.Matrix4): number[] {
  return m.elements.slice();
}
